import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Flex, hasTextContent, Icon, type SvgIcon } from '@bridebook/aura';
import { cn } from '../../../lib/utils';
import { Loader } from '@bridebook/aura/src/icons';
import { ButtonHTMLAttributes, forwardRef, useId } from 'react';
import {
  buttonDisabledClasses,
  buttonFocusClasses,
  buttonIconVariants,
  buttonLayoutClasses,
  buttonSizeConfig,
  buttonSpinnerSizeConfig,
  iconSizeMap,
  type RegularButtonSize,
} from '../button-shared';

const buttonAiSecondaryVariants = cva(
  cn(
    'group',
    buttonLayoutClasses,
    'font-semibold',
    // gradient border: white fills content area, gradient shows through the transparent 1px border
    'button--ai-secondary border border-transparent',
    // icon gradient stop colors — inherited by the inline SVG <linearGradient> inside the button
    '[--aisb-c1:var(--color-electric-violet)] [--aisb-c2:var(--color-indigo-crush)]',
    'hover:[--aisb-c1:var(--color-electric-violet)] hover:[--aisb-c2:var(--color-indigo-crush)]',
    'active:[--aisb-c1:var(--color-electric-violet)] active:[--aisb-c2:var(--color-indigo-crush)]',
    'disabled:[--aisb-c1:var(--color-electric-violet-40)] disabled:[--aisb-c2:var(--color-indigo-crush-40)]',
    buttonFocusClasses,
    buttonDisabledClasses,
  ),
  {
    variants: {
      size: buttonSizeConfig,
    },
  },
);

const aiSecondarySpinnerVariants = cva('ai-secondary-button-icon animate-spin', {
  variants: {
    size: buttonSpinnerSizeConfig,
  },
});

const gradientTextClasses = cn(
  // clip gradient background to glyph outlines
  'bg-clip-text text-transparent',
  // extra bottom space so descenders (g, y, p…) aren't cut off by the clipping box;
  // negative margin cancels the layout shift so flex centering stays unchanged
  'pb-[0.25em] -mb-[0.25em]',
  // gradient per state
  'bg-ai-gradient',
  'group-hover:bg-ai-gradient',
  'group-active:bg-ai-gradient',
  'group-disabled:bg-ai-gradient-40',
);

export interface ButtonAiSecondaryProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children?: React.ReactNode;
  icon?: SvgIcon;
  iconRight?: SvgIcon;
  loading?: boolean;
  size?: RegularButtonSize;
}

export const ButtonAiSecondary = forwardRef<HTMLButtonElement, ButtonAiSecondaryProps>(
  (
    {
      className,
      style,
      loading = false,
      children,
      disabled,
      size = 'standard',
      asChild = false,
      icon,
      iconRight,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const id = useId();
    const gradId = `aisb${id.replace(/[^a-zA-Z0-9]/g, '')}`;

    const isIconOnly = !hasTextContent(children) && icon && !iconRight;
    const sizeToUse = isIconOnly ? iconSizeMap[size] : size;

    return (
      <Comp
        className={cn(buttonAiSecondaryVariants({ size: sizeToUse }), className)}
        style={{ '--icon-fill': `url(#${gradId})`, ...style } as React.CSSProperties}
        ref={ref}
        disabled={loading || disabled}
        {...props}>
        <svg aria-hidden className="pointer-events-none absolute size-0 overflow-hidden">
          <defs>
            {/* top-left to bottom-right, equivalent to 135deg */}
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--aisb-c1)" />
              <stop offset="100%" stopColor="var(--aisb-c2)" />
            </linearGradient>
          </defs>
        </svg>
        {icon && (
          <Icon
            icon={icon}
            className={cn(
              buttonIconVariants({ size: sizeToUse }),
              'ai-secondary-button-icon',
              loading && 'invisible',
            )}
          />
        )}
        <Slottable>
          {hasTextContent(children) ? (
            <span className={cn(gradientTextClasses, loading && 'invisible')}>{children}</span>
          ) : (
            children
          )}
        </Slottable>
        {loading && (
          <Flex className="absolute inset-0 w-full items-center justify-center">
            <Icon icon={Loader} className={aiSecondarySpinnerVariants({ size: sizeToUse })} />
          </Flex>
        )}
        {iconRight && (
          <Icon
            icon={iconRight}
            className={cn(
              buttonIconVariants({ size: sizeToUse }),
              'ai-secondary-button-icon',
              loading && 'invisible',
            )}
          />
        )}
      </Comp>
    );
  },
);
ButtonAiSecondary.displayName = 'ButtonAiSecondary';
